import { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Funcionario } from '@/context/FuncionariosContext';
import { supabase } from '@/lib/supabase';
import { useProgressoes } from '@/hooks/useProgressoes';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { pontosParaAvaliacao, PONTOS_HISTORICO, type PontosEscalasConfig } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';
import AnularProgressaoModal from './AnularProgressaoModal';
import EditarProgressaoModal from './EditarProgressaoModal';

interface HistoricoModalProps {
  funcionario: Funcionario;
  onClose: () => void;
}

type ClassificacaoServico = 'Excelente' | 'Muito Bom' | 'Bom' | 'Relevante';
type AvaliacaoSiadap = 'Excelente' | 'Muito Bom' | 'Bom' | 'Regular' | 'Inadequado' | 'Adequado' | 'Relevante';
type Tab = 'avaliacoes' | 'progressoes';

interface RegistoHistorico {
  id: string;
  ano: number;
  periodoAvaliacao?: string;
  avaliacao?: AvaliacaoSiadap;
  pontuacaoChefe: number;
  pontuacaoCCA: number;
  classificacaoServico?: ClassificacaoServico;
  fonte: 'siadap' | 'historico';
}

const avaliacaoConfig: Record<string, { bg: string; text: string; dot: string; bar: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   bar: 'bg-amber-400'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-400'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400',  bar: 'bg-orange-400'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500',    bar: 'bg-rose-500'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-400'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500',    bar: 'bg-teal-500'    },
};

const classConfig: Record<ClassificacaoServico, { bg: string; text: string; dot: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  Bom:         { bg: 'bg-cyan-50',    text: 'text-cyan-700',    dot: 'bg-cyan-400'    },
  Relevante:   { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
};

const avatarPalette = [
  { bg: 'bg-rose-100',   text: 'text-rose-600'   },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100',  text: 'text-amber-600'  },
  { bg: 'bg-teal-100',   text: 'text-teal-600'   },
  { bg: 'bg-cyan-100',   text: 'text-cyan-600'   },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getAvatarPalette(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarPalette.length;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

export default function HistoricoModal({ funcionario, onClose }: HistoricoModalProps) {
  const { settings } = useAppSettings();
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow();
  const [activeTab, setActiveTab] = useState<Tab>('avaliacoes');
  const [registos, setRegistos] = useState<RegistoHistorico[]>([]);
  const [loading, setLoading] = useState(true);
  const [anoFilter, setAnoFilter] = useState('');

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente: settings.pontos_excelente, 'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom, Regular: settings.pontos_regular, Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023, 'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023, Regular: settings.pontos_regular_2023, Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente: settings.pontos_excelente_pre2023, Relevante: settings.pontos_relevante_pre2023,
      Adequado: settings.pontos_adequado_pre2023, Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023, Bom: settings.pontos_adequado_pre2023,
      Regular: settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const getPontosAvaliacao = useCallback((avaliacao: string, periodoAvaliacao?: string): number => {
    if (!periodoAvaliacao) return PONTOS_HISTORICO[avaliacao] ?? 0;
    return pontosParaAvaliacao(avaliacao, periodoAvaliacao, undefined, escalas);
  }, [escalas]);

  const { progressoes: progressoesBase, loading: loadingProgressoes } = useProgressoes(funcionario.id);
  const [progressoesOverride, setProgressoesOverride] = useState<typeof progressoesBase | null>(null);
  const progressoes = progressoesOverride ?? progressoesBase;

  type ProgressaoItem = (typeof progressoesBase)[0];
  const [anularModal, setAnularModal] = useState<ProgressaoItem | null>(null);
  const [editarModal, setEditarModal] = useState<ProgressaoItem | null>(null);

  const loadHistorico = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: siadapData }, { data: historicoData }] = await Promise.all([
        supabase.from('registos_siadap').select('*').eq('funcionario_id', funcionario.id).order('created_at'),
        supabase.from('historico_progressoes').select('*').eq('funcionario_id', funcionario.id).order('ano', { ascending: false }),
      ]);

      const siadapItems: RegistoHistorico[] = (siadapData ?? []).map((row) => {
        const periodo = (row.periodo_avaliacao as string) ?? '';
        const anoMatch = periodo.match(/(\d{4})/);
        const ano = anoMatch ? parseInt(anoMatch[1], 10) : new Date().getFullYear();
        return {
          id: `siadap-${row.id}`,
          ano,
          periodoAvaliacao: row.periodo_avaliacao as string,
          avaliacao: row.avaliacao as AvaliacaoSiadap,
          pontuacaoChefe: Number(row.pontos_chefe),
          pontuacaoCCA: Number(row.pontos_cca),
          fonte: 'siadap',
        };
      });

      const historicoItems: RegistoHistorico[] = (historicoData ?? []).map((row) => ({
        id: row.id as string,
        ano: row.ano as number,
        pontuacaoChefe: Number(row.pontuacao_chefe),
        pontuacaoCCA: Number(row.pontuacao_cca),
        classificacaoServico: row.classificacao_servico as ClassificacaoServico,
        fonte: 'historico',
      }));

      setRegistos([...siadapItems, ...historicoItems].sort((a, b) => b.ano - a.ano));
    } finally {
      setLoading(false);
    }
  }, [funcionario.id]);

  useEffect(() => { loadHistorico(); }, [loadHistorico]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleClose]);

  const anos = useMemo(() => Array.from(new Set(registos.map((r) => r.ano))).sort((a, b) => b - a), [registos]);
  const filtered = useMemo(() => anoFilter ? registos.filter((r) => r.ano === parseInt(anoFilter, 10)) : registos, [registos, anoFilter]);

  const mediaChefe = filtered.length > 0 ? (filtered.reduce((s, r) => s + r.pontuacaoChefe, 0) / filtered.length).toFixed(2) : '—';
  const mediaCCA   = filtered.length > 0 ? (filtered.reduce((s, r) => s + r.pontuacaoCCA,   0) / filtered.length).toFixed(2) : '—';
  const totalPontosSiadap = filtered.filter((r) => r.fonte === 'siadap' && r.avaliacao).reduce((s, r) => s + getPontosAvaliacao(r.avaliacao!, r.periodoAvaliacao), 0);
  const totalPontosDescontados = progressoes.reduce((s, p) => s + p.pontosDescontados, 0);

  const palette = getAvatarPalette(funcionario.id);
  const initials = getInitials(funcionario.nome);

  const tabs: { id: Tab; label: string; icon: string; count?: number }[] = [
    { id: 'avaliacoes',  label: 'Avaliações',  icon: 'ri-bar-chart-line',       count: registos.length    },
    { id: 'progressoes', label: 'Progressões', icon: 'ri-arrow-up-circle-line',  count: progressoes.length },
  ];

  const modal = (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
        <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
        <div
          className={`relative bg-white rounded-2xl w-full max-w-4xl max-h-[96vh] flex flex-col overflow-hidden border border-zinc-100 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
          onClick={(e) => e.stopPropagation()}
        >

          {/* ── HEADER ── */}
          <div className="px-6 py-5 border-b border-zinc-100 flex items-center gap-4 flex-shrink-0 bg-zinc-50/60">
            <div className={`w-11 h-11 flex items-center justify-center rounded-full text-[14px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Ficha do Funcionário</p>
              <h2 className="font-bold text-zinc-900 text-[16px] leading-tight truncate">{funcionario.nome}</h2>
              <p className="text-[12px] text-zinc-400 mt-0.5">{funcionario.carreira} · <span className="font-mono">#{funcionario.id}</span></p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* ── TABS ── */}
          <div className="px-6 border-b border-zinc-100 flex items-center gap-0 bg-white flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap flex items-center gap-2 px-4 py-3.5 text-[13px] font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-zinc-900 text-zinc-900'
                    : 'border-transparent text-zinc-400 hover:text-zinc-600'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`} />
                </div>
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                    activeTab === tab.id ? 'bg-zinc-100 text-zinc-700' : 'bg-zinc-100 text-zinc-400'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── TAB: AVALIAÇÕES ── */}
          {activeTab === 'avaliacoes' && (
            <>
              {/* Stats bar */}
              <div className="px-6 py-3 bg-zinc-50/40 border-b border-zinc-100 flex items-center gap-5 flex-wrap flex-shrink-0">
                {[
                  { icon: 'ri-history-line', label: 'Registos', value: registos.length, color: 'text-zinc-500' },
                  { icon: 'ri-user-star-line', label: 'Média Chefe', value: mediaChefe, color: 'text-zinc-500' },
                  { icon: 'ri-shield-check-line', label: 'Média CCA', value: mediaCCA, color: 'text-emerald-600' },
                  ...(totalPontosSiadap > 0 ? [{ icon: 'ri-award-line', label: 'Pts SIADAP', value: `${totalPontosSiadap.toFixed(1)}`, color: 'text-amber-600' }] : []),
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <div className={`w-4 h-4 flex items-center justify-center ${s.color}`}>
                      <i className={`${s.icon} text-xs`} />
                    </div>
                    <span className="text-[12px] text-zinc-500">{s.label}: <strong className={`${s.color}`}>{s.value}</strong></span>
                  </div>
                ))}
              </div>

              {/* Filter bar */}
              <div className="px-6 py-2.5 border-b border-zinc-100 flex items-center gap-3 flex-shrink-0">
                <select
                  value={anoFilter}
                  onChange={(e) => setAnoFilter(e.target.value)}
                  className="text-[12px] border border-zinc-200 rounded-lg px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                >
                  <option value="">Todos os anos ({registos.length})</option>
                  {anos.map((a) => (
                    <option key={a} value={a}>{a} ({registos.filter((r) => r.ano === a).length})</option>
                  ))}
                </select>
                {anoFilter && (
                  <button onClick={() => setAnoFilter('')} className="whitespace-nowrap flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">
                    <i className="ri-close-circle-line text-sm" />Limpar
                  </button>
                )}
                <span className="ml-auto text-[11px] text-zinc-400">
                  <span className="font-semibold text-zinc-600">{filtered.length}</span> de {registos.length}
                </span>
              </div>

              {/* Table */}
              <div className="overflow-y-auto flex-1">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" /></div>
                    <p className="text-[13px] text-zinc-400">A carregar...</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white z-10 border-b border-zinc-100">
                      <tr>
                        {['Ano', 'Avaliação', 'Chefe', 'CCA', 'Fonte'].map((h) => (
                          <th key={h} className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, i) => {
                        const isSiadap = r.fonte === 'siadap';
                        const avalCfg = r.avaliacao ? avaliacaoConfig[r.avaliacao] : null;
                        const classCfg = r.classificacaoServico ? classConfig[r.classificacaoServico] : null;
                        const pts = r.avaliacao ? getPontosAvaliacao(r.avaliacao, r.periodoAvaliacao) : null;
                        const isFirst = i === 0 && !anoFilter;
                        return (
                          <tr key={r.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors ${isFirst ? 'bg-emerald-50/20' : ''}`}>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                {isFirst && (
                                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Último</span>
                                )}
                                <span className="text-[13px] font-semibold text-zinc-700">{r.ano}</span>
                                {isSiadap && r.periodoAvaliacao && (
                                  <span className="text-[11px] text-zinc-400">({r.periodoAvaliacao})</span>
                                )}
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              {isSiadap && avalCfg && r.avaliacao ? (
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${avalCfg.bg} ${avalCfg.text}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${avalCfg.dot}`} />
                                    {r.avaliacao}
                                  </span>
                                  {pts !== null && (
                                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${avalCfg.bg} ${avalCfg.text}`}>
                                      {pts % 1 === 0 ? pts.toFixed(0) : pts.toFixed(1)} pts
                                    </span>
                                  )}
                                </div>
                              ) : classCfg && r.classificacaoServico ? (
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${classCfg.bg} ${classCfg.text}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${classCfg.dot}`} />
                                  {r.classificacaoServico}
                                </span>
                              ) : <span className="text-[12px] text-zinc-300">—</span>}
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-zinc-400 rounded-full" style={{ width: `${(r.pontuacaoChefe / 5) * 100}%` }} />
                                </div>
                                <span className="text-[12px] font-medium text-zinc-700">{r.pontuacaoChefe.toFixed(1)}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(r.pontuacaoCCA / 5) * 100}%` }} />
                                </div>
                                <span className="text-[12px] font-medium text-zinc-700">{r.pontuacaoCCA.toFixed(1)}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              {isSiadap ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />SIADAP
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />Manual
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-5 py-16 text-center">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-10 h-10 flex items-center justify-center bg-zinc-50 rounded-full">
                                <i className="ri-history-line text-xl text-zinc-300" />
                              </div>
                              <p className="text-[13px] text-zinc-400">{anoFilter ? `Sem registos para ${anoFilter}.` : 'Sem registos históricos.'}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* ── TAB: PROGRESSÕES ── */}
          {activeTab === 'progressoes' && (
            <>
              {/* Stats bar */}
              <div className="px-6 py-3 bg-zinc-50/40 border-b border-zinc-100 flex items-center gap-5 flex-wrap flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-emerald-500"><i className="ri-arrow-up-circle-line text-xs" /></div>
                  <span className="text-[12px] text-zinc-500"><strong className="text-zinc-700">{progressoes.length}</strong> progressão(ões)</span>
                </div>
                {progressoes.length > 0 && (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-arrow-up-line text-xs" /></div>
                      <span className="text-[12px] text-zinc-500">Nível atual: <strong className="text-zinc-700">{progressoes[progressoes.length - 1].nivelNovo}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center text-rose-400"><i className="ri-subtract-line text-xs" /></div>
                      <span className="text-[12px] text-zinc-500">Total descontado: <strong className="text-rose-600">{totalPontosDescontados % 1 === 0 ? totalPontosDescontados.toFixed(0) : totalPontosDescontados.toFixed(1)} pts</strong></span>
                    </div>
                  </>
                )}
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-5">
                {loadingProgressoes ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" /></div>
                    <p className="text-[13px] text-zinc-400">A carregar...</p>
                  </div>
                ) : progressoes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-14 h-14 flex items-center justify-center bg-zinc-50 rounded-full border border-zinc-100">
                      <i className="ri-arrow-up-circle-line text-3xl text-zinc-300" />
                    </div>
                    <p className="text-[13px] font-medium text-zinc-500">Sem progressões registadas</p>
                    <p className="text-[12px] text-zinc-400 text-center max-w-xs">As progressões aparecerão aqui após serem registadas.</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[19px] top-6 bottom-6 w-px bg-zinc-100" />
                    <div className="space-y-2">
                      {[...progressoes].reverse().map((p, i) => {
                        const isFirst = i === 0;
                        const isLast = i === progressoes.length - 1;
                        return (
                          <div key={p.id} className="relative flex gap-4 items-start">
                            {/* Timeline dot */}
                            <div className={`relative z-10 w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 mt-1 ${isFirst ? 'bg-emerald-100 text-emerald-600' : 'bg-zinc-100 text-zinc-500'}`}>
                              <i className="ri-arrow-up-line text-sm" />
                              {isFirst && <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />}
                            </div>

                            {/* Card */}
                            <div className={`flex-1 rounded-xl border p-4 mb-2 ${isFirst ? 'border-emerald-100 bg-emerald-50/20' : 'border-zinc-100 bg-white'}`}>
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* Nível */}
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[12px] text-zinc-500 font-medium">Nível {p.nivelAnterior}</span>
                                    <div className="w-4 h-4 flex items-center justify-center text-emerald-500">
                                      <i className="ri-arrow-right-line text-sm" />
                                    </div>
                                    <span className="text-[13px] font-bold text-emerald-700">Nível {p.nivelNovo}</span>
                                    <span className="text-[11px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">+{p.nivelNovo - p.nivelAnterior}</span>
                                  </div>
                                  {/* Badges */}
                                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${progressoes.length - i > 1 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-zinc-100 text-zinc-500'}`}>
                                    {progressoes.length - i > 1 && <i className="ri-repeat-line text-[10px]" />}
                                    {progressoes.length - i}ª progressão
                                  </span>
                                  {isFirst && <span className="text-[11px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">Mais recente</span>}
                                  {isLast && progressoes.length > 1 && <span className="text-[11px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full font-medium">Primeira</span>}
                                </div>
                                {/* Pontos descontados */}
                                <span className="inline-flex items-center gap-1 text-[12px] font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg flex-shrink-0">
                                  <i className="ri-subtract-line text-xs" />
                                  {p.pontosDescontados % 1 === 0 ? p.pontosDescontados.toFixed(0) : p.pontosDescontados.toFixed(1)} pts
                                </span>
                              </div>

                              {/* Data */}
                              <div className="flex items-center gap-1.5 mt-2">
                                <div className="w-3.5 h-3.5 flex items-center justify-center text-zinc-300">
                                  <i className="ri-calendar-check-line text-xs" />
                                </div>
                                <span className="text-[11px] text-zinc-400">{formatDate(p.dataProgressao)}</span>
                              </div>

                              {/* Observações */}
                              {p.observacoes && (
                                <div className="mt-2.5 pt-2.5 border-t border-zinc-100 flex items-start gap-2">
                                  <div className="w-3.5 h-3.5 flex items-center justify-center text-zinc-300 flex-shrink-0 mt-0.5">
                                    <i className="ri-file-text-line text-xs" />
                                  </div>
                                  <p className="text-[11px] text-zinc-500 italic leading-relaxed">{p.observacoes}</p>
                                </div>
                              )}

                              {/* Ações */}
                              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setEditarModal(p)}
                                  className="whitespace-nowrap inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-600 hover:text-amber-800 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer border border-amber-200 hover:border-amber-300"
                                >
                                  <i className="ri-edit-line text-sm" />Editar
                                </button>
                                <button
                                  onClick={() => setAnularModal(p)}
                                  className="whitespace-nowrap inline-flex items-center gap-1.5 text-[12px] font-medium text-rose-500 hover:text-rose-700 hover:bg-rose-50 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer border border-rose-200 hover:border-rose-300"
                                >
                                  <i className="ri-arrow-go-back-line text-sm" />Anular
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer summary */}
                    <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between flex-wrap gap-3">
                      <p className="text-[11px] text-zinc-400">
                        {progressoes.length} progressão(ões) · nível {progressoes[0].nivelAnterior} → {progressoes[progressoes.length - 1].nivelNovo}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-rose-600 bg-rose-50 px-3 py-1.5 rounded-lg">
                        <i className="ri-subtract-line text-xs" />
                        {totalPontosDescontados % 1 === 0 ? totalPontosDescontados.toFixed(0) : totalPontosDescontados.toFixed(1)} pts descontados
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {anularModal && (
        <AnularProgressaoModal
          progressao={{
            id: anularModal.id,
            funcionarioId: anularModal.funcionarioId,
            funcionarioNome: funcionario.nome,
            dataProgressao: anularModal.dataProgressao,
            nivelAnterior: anularModal.nivelAnterior,
            nivelNovo: anularModal.nivelNovo,
            pontosDescontados: anularModal.pontosDescontados,
          }}
          onClose={() => setAnularModal(null)}
          onSuccess={() => {
            const idAnulado = anularModal.id;
            setAnularModal(null);
            setProgressoesOverride((prev) => (prev ?? progressoesBase).filter((p) => p.id !== idAnulado));
          }}
        />
      )}
      {editarModal && (
        <EditarProgressaoModal
          progressao={{
            id: editarModal.id,
            funcionarioId: editarModal.funcionarioId,
            funcionarioNome: funcionario.nome,
            dataProgressao: editarModal.dataProgressao,
            nivelAnterior: editarModal.nivelAnterior,
            nivelNovo: editarModal.nivelNovo,
            pontosDescontados: editarModal.pontosDescontados,
            observacoes: editarModal.observacoes,
          }}
          onClose={() => setEditarModal(null)}
          onSuccess={(updated) => {
            setEditarModal(null);
            setProgressoesOverride((prev) =>
              (prev ?? progressoesBase).map((p) =>
                p.id === updated.id
                  ? { ...p, dataProgressao: updated.dataProgressao, nivelNovo: updated.nivelNovo, observacoes: updated.observacoes }
                  : p
              )
            );
          }}
        />
      )}
    </>
  );

  return createPortal(modal, document.body);
}